import { useCallback, useEffect, useState } from 'react';
import { adminEmpresasService } from '@/services';
import { empresasService } from '@/services';
import type { EmpresaSysadmin } from '@/services/sysadmin.service';
import type { Empresa } from '@/types/types';

interface UseEmpresasResult {
  empresas: Empresa[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useEmpresas(): UseEmpresasResult {
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEmpresas = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await empresasService.getAll();
      setEmpresas(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar empresas');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmpresas();
  }, [fetchEmpresas]);

  return { empresas, isLoading, error, refetch: fetchEmpresas };
}

interface UseEmpresaResult {
  empresa: Empresa | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useEmpresa(nit: string): UseEmpresaResult {
  const [empresa, setEmpresa] = useState<Empresa | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEmpresa = useCallback(async () => {
    if (!nit) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await empresasService.getByNit(nit);
      console.log('Fetched empresa:', data);
      setEmpresa(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar empresa');
    } finally {
      setIsLoading(false);
    }
  }, [nit]);

  useEffect(() => {
    fetchEmpresa();
  }, [fetchEmpresa]);

  return { empresa, isLoading, error, refetch: fetchEmpresa };
}
interface UseEmpresasAdminResult {
  empresas: EmpresaSysadmin[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useEmpresasAdmin(): UseEmpresasAdminResult {
  const [empresas, setEmpresas] = useState<EmpresaSysadmin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEmpresas = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await adminEmpresasService.listarTodas();
      if (res.success) {
        setEmpresas(res.empresas);
      } else {
        setError('Error al cargar empresas');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar empresas');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmpresas();
  }, [fetchEmpresas]);

  return { empresas, isLoading, error, refetch: fetchEmpresas };
}
